import { useEffect, useState } from "react";
import { Button, Checkbox, Space, Switch, Table } from "antd";
import { MasterDataServices } from "../../../../services/master-data";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPen } from "@fortawesome/free-solid-svg-icons";
import { useMessageApi } from "../../../../context/MessageContext";
import { PERMISSIONS } from "../../../../constants/permission";
import { useHasPermission } from "../../../../hooks/useHasPermission";
import { IMasterData, IMasterDataListResponse } from "../../../../interfaces/master-data";
import { DEFAULT_PAGINATION } from "../../../../constants/pagination";
import { ISortInfo } from "../../../../interfaces/common";
import { NOMINAL_CODE_TYPES } from "../../../../constants/enum";
import { ColumnsType } from "antd/lib/table";

interface NominalCodesListProps {
  onEditNominalCode: (nominalCode: IMasterData) => void;
  trigger: boolean;
  onDoneTrigger: () => void;
  debouncedSearchValue: string | null;
  type: NOMINAL_CODE_TYPES;
  showActiveOnly: boolean | null;
}

const NominalCodesList: React.FC<NominalCodesListProps> = ({
  onEditNominalCode,
  trigger,
  onDoneTrigger,
  debouncedSearchValue,
  type,
  showActiveOnly,
}) => {
  const messageApi = useMessageApi();
  const [data, setData] = useState<IMasterData[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const hasPermissionEditor = useHasPermission([PERMISSIONS.MASTER_DATA_EDITOR]);

  const [sortInfo, setSortInfo] = useState<ISortInfo>({});
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: DEFAULT_PAGINATION.pageSize,
    total: 0,
  });

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const fetchData = async () => {
    try {
      setLoading(true);
      const params = {
        keyword: debouncedSearchValue || undefined,
        type: type,
        page: pagination.current,
        pageSize: pagination.pageSize,
        orderBy: sortInfo.orderBy ?? "code",
        orderDirection: sortInfo.orderDirection ?? "ASC",
        showActiveOnly: showActiveOnly,
      };
      const response: IMasterDataListResponse = await MasterDataServices.getList(params);
      if (response.success) {
        setData(response.data.results);
        setPagination((prev) => ({
          ...prev,
          total: response.data.meta?.totalElements || 0,
        }));
      }
    } catch (error) {
      console.error("Fetch nominal code error:", error);
    } finally {
      setLoading(false);
      onDoneTrigger();
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearchValue, pagination.current, pagination.pageSize, sortInfo, trigger, showActiveOnly]);

  const handleTableChange = (paginationConfig: any, filters: any, sorter: any) => {
    // Handle pagination
    setPagination((prev) => ({
      ...prev,
      current: paginationConfig.pageSize !== prev.pageSize ? 1 : paginationConfig.current,
      pageSize: paginationConfig.pageSize,
    }));

    // Handle sorting
    if (sorter?.field) {
      setSortInfo({
        orderBy: sorter.field,
        orderDirection: sorter.order === "ascend" ? "asc" : "desc",
      });
    } else {
      // Clear sorting
      setSortInfo({});
    }
  };

  const columns: ColumnsType<IMasterData> = [
    {
      title:
        type === NOMINAL_CODE_TYPES.CREDIT ? "Credit" : type === NOMINAL_CODE_TYPES.DEBIT ? "Debit" : "Unders & Overs",
      dataIndex: "code",
      key: "code",
      sorter: true,
    },
    {
      title: "Active",
      dataIndex: "isActive",
      key: "isActive",
      width: 100,
      render: (_: any, record: IMasterData) => (
        <Switch
          checked={record.isActive}
          disabled={!hasPermissionEditor}
          onChange={() => handleIsActivesNominalCode(record)}
        />
      ),
    },
    {
      title: "Actions",
      key: "action",
      width: 100,
      render: (_: any, record: IMasterData) =>
        hasPermissionEditor ? (
          <Space size="small" className="box-action" align="center">
            <Button type="link" onClick={() => onEditNominalCode(record)}>
              <FontAwesomeIcon icon={faPen} />
            </Button>
          </Space>
        ) : null,
    },
  ];

  if (type === NOMINAL_CODE_TYPES.DEBIT) {
    const premiereGolfNominalCol: ColumnsType<IMasterData>[number] = {
      title: "Premiere Golf Nominal",
      dataIndex: "premiereGolfNominal",
      key: "premiereGolfNominal",
      width: 200,
      align: "center",
      render: (_: any, record: IMasterData) => (
        <Checkbox
          checked={!!record.premiereGolfNominal}
        />
      ),
    };
    columns.splice(1, 0, premiereGolfNominalCol);
  }

  const handleIsActivesNominalCode = async (selectNominalCode: IMasterData) => {
    try {
      const newStatus = !selectNominalCode.isActive;
      const res: any = await MasterDataServices.update(selectNominalCode?.id?.toString(), {
        type: selectNominalCode.type,
        code: selectNominalCode.code,
        isActive: newStatus,
      });
      if (res?.success) {
        messageApi.success(`Successfully ${selectNominalCode.isActive ? "deactivated" : "activated"}`);
        setData((prev) =>
          prev.map((item) => (item.id === selectNominalCode.id ? { ...item, isActive: newStatus } : item))
        );
      }
    } catch (error: any) {
      messageApi.error(error?.message);
    }
  };

  return (
    <Table
      dataSource={data}
      loading={loading}
      rowKey={(record) => record.id}
      columns={columns}
      pagination={{
        current: pagination.current,
        pageSize: pagination.pageSize,
        total: pagination.total,
        showSizeChanger: true,
        showQuickJumper: true,
        showTotal: (total, range) => `Total ${total} items`,
      }}
      onChange={handleTableChange}
      size="small"
      className="table-fm"
      rowClassName={(record, index) => (index % 2 === 0 ? "highlight-row" : "")}
    />
  );
};

export default NominalCodesList;
